import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Plus, MagnifyingGlass, X, Warning, Check } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const formatCLP = (amount) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const AdvancesPage = () => {
  const { axiosInstance, isAdmin, user } = useAuth();
  const [advances, setAdvances] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingAdvance, setCancellingAdvance] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [workerSearch, setWorkerSearch] = useState('');

  // Form
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [workerSearchInput, setWorkerSearchInput] = useState('');
  const [filteredWorkers, setFilteredWorkers] = useState([]);
  const [amount, setAmount] = useState('');
  const [observation, setObservation] = useState('');
  const [advanceDate, setAdvanceDate] = useState(new Date());
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [saving, setSaving] = useState(false);
  const [allowEditPastDays, setAllowEditPastDays] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 100 };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (branchFilter) params.branch_id = branchFilter;

      const [advancesRes, workersRes, branchesRes, settingsRes] = await Promise.all([
        axiosInstance.get('/api/advances', { params }),
        axiosInstance.get('/api/workers', { params: { status: 'activo' } }),
        axiosInstance.get('/api/branches'),
        axiosInstance.get('/api/settings'),
      ]);
      setAdvances(advancesRes.data.advances);
      setTotal(advancesRes.data.total);
      setWorkers(workersRes.data);
      setBranches(branchesRes.data);
      setAllowEditPastDays(settingsRes.data?.allow_edit_past_days ?? true);
    } catch (error) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, dateFrom, dateTo, branchFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (workerSearchInput.length >= 2) {
      const filtered = workers.filter(
        (w) =>
          w.first_name.toLowerCase().includes(workerSearchInput.toLowerCase()) ||
          w.last_name.toLowerCase().includes(workerSearchInput.toLowerCase()) ||
          w.code.toLowerCase().includes(workerSearchInput.toLowerCase())
      );
      setFilteredWorkers(filtered.slice(0, 10));
    } else {
      setFilteredWorkers([]);
    }
  }, [workerSearchInput, workers]);

  const handleOpenModal = () => {
    setSelectedWorker(null);
    setWorkerSearchInput('');
    setAmount('');
    setObservation('');
    setAdvanceDate(new Date());
    setError('');
    setWarning('');
    setShowModal(true);
  };

  const handleSelectWorker = (worker) => {
    setSelectedWorker(worker);
    setWorkerSearchInput(`${worker.code} - ${worker.first_name} ${worker.last_name}`);
    setFilteredWorkers([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedWorker) {
      setError('Debe seleccionar un trabajador');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      setError('El monto debe ser mayor a 0');
      return;
    }

    // Block past dates for non-admin
    if (!isAdmin && !allowEditPastDays) {
      const todayStr = new Date().toISOString().split('T')[0];
      const selectedStr = format(advanceDate, 'yyyy-MM-dd');
      if (selectedStr < todayStr) {
        setError('La edicion de dias anteriores esta desactivada por administracion. No puede registrar anticipos en fechas pasadas.');
        return;
      }
    }

    setError('');
    setWarning('');
    setSaving(true);

    try {
      const response = await axiosInstance.post('/api/advances', {
        worker_id: selectedWorker.id,
        amount: parseFloat(amount),
        observation: observation || null,
        date: format(advanceDate, 'yyyy-MM-dd'),
      });

      if (response.data.warning) {
        setWarning(response.data.warning);
      }

      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrar anticipo');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelAdvance = async () => {
    if (!cancellingAdvance) return;
    try {
      await axiosInstance.delete(`/api/advances/${cancellingAdvance.id}?reason=${encodeURIComponent(cancelReason)}`);
      setShowCancelModal(false);
      setCancellingAdvance(null);
      setCancelReason('');
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al anular anticipo');
    }
  };

  const openCancelModal = (advance) => {
    setCancellingAdvance(advance);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const getBranchName = (branchId) => {
    const branch = branches.find((b) => b.id === branchId);
    return branch?.name || '';
  };

  const today = format(new Date(), 'yyyy-MM-dd');

  return (
    <div className="space-y-6" data-testid="advances-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-900" data-testid="advances-title">
            Anticipos
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Registro y control de anticipos de personal
          </p>
        </div>
        <button
          onClick={handleOpenModal}
          className="btn-primary flex items-center gap-2"
          data-testid="register-advance-btn"
        >
          <Plus size={18} />
          Registrar Anticipo
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div>
          <label className="form-label">Desde</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="form-input w-40"
            data-testid="date-from-filter"
          />
        </div>
        <div>
          <label className="form-label">Hasta</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="form-input w-40"
            data-testid="date-to-filter"
          />
        </div>
        {isAdmin && (
          <div>
            <label className="form-label">Sucursal</label>
            <select
              value={branchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
              className="form-select w-48"
              data-testid="branch-filter"
            >
              <option value="">Todas</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="flex items-center gap-4 text-sm text-zinc-600">
        <span>
          Mostrando <strong>{advances.length}</strong> de <strong>{total}</strong> anticipos
        </span>
      </div>

      {/* Advances Table */}
      <div className="table-container">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner"></div>
          </div>
        ) : advances.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            No se encontraron anticipos
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="advances-table">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Fecha
                  </th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Código
                  </th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Trabajador
                  </th>
                  {isAdmin && (
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                      Sucursal
                    </th>
                  )}
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Monto
                  </th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Observación
                  </th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Registrado por
                  </th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {advances.map((advance) => (
                  <tr
                    key={advance.id}
                    className={`data-row border-b border-zinc-100 ${advance.is_cancelled ? 'opacity-50' : ''}`}
                  >
                    <td className="px-4 py-3 text-sm font-mono text-zinc-600">
                      {advance.date}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-zinc-600">
                      {advance.worker_code}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-zinc-900">
                      {advance.worker_name}
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-sm text-zinc-600">
                        {getBranchName(advance.branch_id)}
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm font-mono text-right text-zinc-900">
                      {advance.is_cancelled ? (
                        <span className="line-through text-zinc-400">{formatCLP(advance.amount)}</span>
                      ) : (
                        formatCLP(advance.amount)
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-600 max-w-xs truncate">
                      {advance.is_cancelled ? (
                        <span className="text-red-600">Anulado: {advance.cancel_reason || 'Sin motivo'}</span>
                      ) : (
                        advance.observation || '-'
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-600">
                      {advance.created_by_name}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!advance.is_cancelled && (advance.date === today || isAdmin) && (
                        <button
                          onClick={() => openCancelModal(advance)}
                          className="text-xs text-red-600 hover:text-red-800 transition-colors"
                          data-testid={`cancel-advance-${advance.id}`}
                        >
                          Anular
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Register Advance Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-heading">Registrar Anticipo</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="alert-error text-sm">{error}</div>}
            {warning && <div className="alert-warning text-sm">{warning}</div>}

            {/* Worker Search */}
            <div className="relative">
              <label className="form-label">Trabajador</label>
              <div className="relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="text"
                  value={workerSearchInput}
                  onChange={(e) => {
                    setWorkerSearchInput(e.target.value);
                    if (selectedWorker) setSelectedWorker(null);
                  }}
                  placeholder="Buscar por nombre o código..."
                  className="form-input pl-10"
                  data-testid="worker-search-input"
                />
                {selectedWorker && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedWorker(null);
                      setWorkerSearchInput('');
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
              {filteredWorkers.length > 0 && !selectedWorker && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-zinc-200 max-h-48 overflow-auto">
                  {filteredWorkers.map((worker) => (
                    <button
                      key={worker.id}
                      type="button"
                      onClick={() => handleSelectWorker(worker)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-zinc-50 flex justify-between"
                      data-testid={`worker-option-${worker.id}`}
                    >
                      <span>
                        <span className="font-mono text-zinc-500">{worker.code}</span> -{' '}
                        {worker.first_name} {worker.last_name}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Date */}
            <div>
              <label className="form-label">Fecha</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="form-input w-full text-left"
                    data-testid="advance-date-picker"
                  >
                    {format(advanceDate, "d 'de' MMMM, yyyy", { locale: es })}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-none" align="start">
                  <Calendar
                    mode="single"
                    selected={advanceDate}
                    onSelect={(date) => date && setAdvanceDate(date)}
                    locale={es}
                    className="rounded-none"
                    disabled={!isAdmin && !allowEditPastDays ? (date) => date < new Date(new Date().setHours(0,0,0,0)) : undefined}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Amount */}
            <div>
              <label className="form-label">Monto (CLP)</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                min="1"
                className="form-input font-mono"
                required
                data-testid="advance-amount-input"
              />
            </div>

            {/* Observation */}
            <div>
              <label className="form-label">Observación (opcional)</label>
              <textarea
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                rows={2}
                className="form-input resize-none"
                placeholder="Agregar nota o comentario..."
                data-testid="advance-observation-input"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                className="rounded-none"
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} className="rounded-none bg-zinc-900">
                {saving ? 'Guardando...' : 'Registrar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cancel Advance Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="sm:max-w-md rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-red-600">
              <Warning size={20} />
              Anular Anticipo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-600">
              ¿Está seguro de anular el anticipo de{' '}
              <strong>{formatCLP(cancellingAdvance?.amount || 0)}</strong> para{' '}
              <strong>{cancellingAdvance?.worker_name}</strong>?
            </p>
            <div>
              <label className="form-label">Motivo de anulación</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={2}
                className="form-input resize-none"
                placeholder="Ingrese el motivo..."
                data-testid="cancel-reason-input"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCancelModal(false)}
                className="rounded-none"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCancelAdvance}
                className="rounded-none bg-red-600 hover:bg-red-700"
                data-testid="confirm-cancel-btn"
              >
                Anular Anticipo
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdvancesPage;
